import { Button, CheckboxField, Modal } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import './AccountingBaseSwitchInformationModal.css';
import { useAccountingSoftwareDisplayName } from 'src/core/modules/accounting-integration/apis';
import { type AccountingSoftware } from 'src/core/modules/bookkeep/integration/status';

type Props = {
  accountingIntegration:
    | AccountingSoftware
    | 'noIntegration'
    | 'switchInProgress';
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export function AccountingBaseBetaWarningModal({
  accountingIntegration,
  isOpen,
  onClose,
  onConfirm,
}: Props) {
  const { t } = useTranslation('global');
  const { getDisplayName } = useAccountingSoftwareDisplayName();

  const [isConfirmInitiallyDisabled, setIsConfirmInitiallyDisabled] =
    useState(true);

  if (accountingIntegration === 'switchInProgress') {
    return null;
  }

  const integration =
    accountingIntegration === 'noIntegration'
      ? ''
      : getDisplayName(accountingIntegration);

  return (
    <Modal
      isOpen={isOpen}
      iconName="plug"
      iconVariant="info"
      title={t('bookkeep.integrations.settings.betaModal.title', {
        integration,
      })}
      subtitle={t('bookkeep.integrations.settings.betaModal.subtitle')}
      actions={[
        <Button
          key="back"
          variant="secondaryNeutral"
          onClick={() => onClose()}
          text={t('misc.cancel')}
        />,
        <Button
          key="confirm"
          text={t('misc.continue')}
          variant="primaryBrand"
          onClick={() => onConfirm()}
          isDisabled={isConfirmInitiallyDisabled}
        />,
      ]}
    >
      <div className="text-content-primary text-left">
        <h3 className="title-l">
          {t('bookkeep.integrations.settings.betaModal.expectHeader')}
        </h3>
        <ul className="text-content-primary mt-8 mb-8 ml-24 list-disc">
          <li>{t('bookkeep.integrations.settings.betaModal.expectPoint1')}</li>
          <li>{t('bookkeep.integrations.settings.betaModal.expectPoint2')}</li>
        </ul>

        <h3 className="title-l">
          {t('bookkeep.integrations.settings.betaModal.feedbackHeader')}
        </h3>
        <p className="text-content-primary mt-8">
          {t('bookkeep.integrations.settings.betaModal.feedbackInfo')}
        </p>
        <ul className="text-content-primary ml-24 list-disc">
          <li>
            {t('bookkeep.integrations.settings.betaModal.feedbackPoint1')}
          </li>
          <li>
            {t('bookkeep.integrations.settings.betaModal.feedbackPoint2')}
          </li>
        </ul>

        <CheckboxField
          className="mt-16 hover:bg-[transparent]"
          isChecked={!isConfirmInitiallyDisabled}
          label={
            <Trans
              values={{ accountingIntegration }}
              i18nKey="bookkeep.integrations.settings.betaModal.checkbox"
            />
          }
          onChange={() =>
            setIsConfirmInitiallyDisabled(!isConfirmInitiallyDisabled)
          }
        />
      </div>
    </Modal>
  );
}
